/**
 * Table de routage de l’application (DECISIONS : routes en français).
 *
 * Exportée séparément de `App` pour que les tests puissent la monter dans un `MemoryRouter`
 * (aucune dépendance à l’URL du navigateur).
 */
import { lazy, Suspense } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';

import { RouteProtegee } from './route-protegee';
import { EcranAccueil } from '../screens/accueil';
import { EcranAmeliorations } from '../screens/ameliorations';
import { EcranConnexion } from '../screens/connexion';
import { EcranGrimoire } from '../screens/grimoire';
import { EcranHistorique } from '../screens/historique';
import { EcranInscription } from '../screens/inscription';
import { EcranIntrouvable } from '../screens/introuvable';
import { EcranJeu } from '../screens/jeu';
import {
  EcranCgu,
  EcranConfidentialite,
  EcranCookies,
  EcranMentionsLegales,
} from '../screens/page-legale';
import { Chargement } from '../ui/Chargement';

/**
 * Page de présentation, chargée **à la demande** : elle embarque le contenu du jeu (niveaux,
 * cartes, tours) pour ses chiffres, ce dont le reste de l'application n'a pas besoin.
 */
const EcranDecouvrir = lazy(async () => {
  const module = await import('../screens/decouvrir');
  return { default: module.EcranDecouvrir };
});

function EcranDecouvrirDiffere() {
  return (
    <Suspense
      fallback={
        <div className="ecran ecran--centree">
          <Chargement />
        </div>
      }
    >
      <EcranDecouvrir />
    </Suspense>
  );
}

/**
 * Écran de jeu **remonté à chaque changement de niveau** (U-01).
 *
 * Passer au niveau suivant change seulement le paramètre de route : sans cette clé, React
 * réutiliserait l'instance et son état (run terminée, références de chargement, marqueur de
 * session). La clé garantit qu'un niveau démarre toujours d'un état neuf, exactement comme un
 * accès direct à son adresse.
 */
function EcranJeuDuNiveau() {
  const { levelId = '' } = useParams<{ levelId: string }>();
  return <EcranJeu key={levelId} />;
}

export const routes = (
  <Routes>
    {/* Présentation du jeu : publique, c'est la porte d'entrée d'un visiteur sans compte. */}
    <Route path="/decouvrir" element={<EcranDecouvrirDiffere />} />
    <Route path="/connexion" element={<EcranConnexion />} />
    <Route path="/inscription" element={<EcranInscription />} />
    {/* Pages légales : publiques, accessibles avant toute création de compte (DECISIONS §11). */}
    <Route path="/mentions-legales" element={<EcranMentionsLegales />} />
    <Route path="/cgu" element={<EcranCgu />} />
    <Route path="/confidentialite" element={<EcranConfidentialite />} />
    <Route path="/cookies" element={<EcranCookies />} />
    <Route
      path="/"
      element={
        <RouteProtegee>
          <EcranAccueil />
        </RouteProtegee>
      }
    />
    <Route
      path="/ameliorations"
      element={
        <RouteProtegee>
          <EcranAmeliorations />
        </RouteProtegee>
      }
    />
    <Route
      path="/historique"
      element={
        <RouteProtegee>
          <EcranHistorique />
        </RouteProtegee>
      }
    />
    <Route
      path="/grimoire"
      element={
        <RouteProtegee>
          <EcranGrimoire />
        </RouteProtegee>
      }
    />
    <Route
      path="/jeu/:levelId"
      element={
        <RouteProtegee>
          <EcranJeuDuNiveau />
        </RouteProtegee>
      }
    />
    <Route path="*" element={<EcranIntrouvable />} />
  </Routes>
);
